<template>
  <main class="min-h-screen bg-white font-sans antialiased text-gray-900 selection:bg-orange-100 selection:text-orange-700">

    <landing-header />

    <!-- HERO -->
    <section class="relative overflow-hidden bg-gray-900 pt-28 pb-24">
      <div class="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>
      <div class="absolute top-0 right-0 w-[600px] h-[600px] bg-orange-600/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
      <div class="absolute bottom-0 left-0 w-[400px] h-[400px] bg-red-600/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/4 pointer-events-none"></div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="max-w-3xl">
          <div class="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 px-4 py-2 rounded-full mb-8">
            <span class="flex h-2 w-2 rounded-full bg-orange-500 animate-ping"></span>
            <span class="text-xs font-black uppercase tracking-widest text-orange-400">Ressources & Guides CVC</span>
          </div>
          <h1 class="text-5xl lg:text-7xl font-black text-white leading-[1.05] mb-6">
            Le Blog<br>
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">Termoflow.</span>
          </h1>
          <p class="text-xl text-gray-300 leading-relaxed max-w-xl">
            Réglementation, conformité F-Gaz, gestion d'équipe terrain… Tout ce qu'un artisan CVC doit savoir.
          </p>
        </div>

        <!-- Search -->
        <div class="mt-12 max-w-2xl">
          <div class="relative">
            <div class="absolute inset-y-0 left-5 flex items-center pointer-events-none">
              <i class="fa-solid fa-magnifying-glass text-gray-400 text-lg"></i>
            </div>
            <input
              v-model="search"
              @input="onSearch"
              type="search"
              placeholder="Rechercher un article, une réglementation…"
              class="w-full pl-14 pr-6 py-5 bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-gray-400 rounded-2xl text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white/15 transition-all"
            />
            <div v-if="searching" class="absolute inset-y-0 right-5 flex items-center">
              <i class="fa-solid fa-circle-notch fa-spin text-orange-400"></i>
            </div>
          </div>
          <div class="flex flex-wrap gap-2 mt-4">
            <button
              v-for="tag in quickTags" :key="tag"
              @click="toggleTag(tag)"
              :class="[
                'px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider transition-all',
                activeTag === tag
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20 border border-white/10'
              ]"
            >{{ tag }}</button>
          </div>
        </div>
      </div>
    </section>

    <!-- TOOLBAR -->
    <div class="bg-orange-50 border-b border-orange-100 py-3 sticky top-0 z-30">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap gap-4 items-center justify-between">
        <p class="text-sm font-bold text-gray-600">
          <template v-if="loading">Chargement…</template>
          <template v-else>
            <span class="text-orange-600 text-base font-black">{{ pagination.total }}</span>
            article{{ pagination.total > 1 ? 's' : '' }}
            <span v-if="search" class="text-gray-400 font-normal"> pour «&nbsp;{{ search }}&nbsp;»</span>
          </template>
        </p>
        <div class="flex items-center gap-3">
          <span class="text-xs font-bold text-gray-400 uppercase tracking-wider hidden sm:block">Trier par</span>
          <div class="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">
            <button
              v-for="s in sortOptions" :key="s.value"
              @click="setSort(s.value)"
              :class="['px-3 py-1.5 rounded-lg text-xs font-black transition-all', sort === s.value ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-800']"
            >{{ s.label }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ARTICLES -->
    <section class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

      <!-- Skeleton -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div v-for="i in 6" :key="i" class="bg-gray-100 rounded-3xl overflow-hidden animate-pulse">
          <div class="h-52 bg-gray-200"></div>
          <div class="p-7 space-y-3">
            <div class="h-3 w-20 bg-gray-200 rounded-full"></div>
            <div class="h-5 bg-gray-200 rounded-full"></div>
            <div class="h-5 w-3/4 bg-gray-200 rounded-full"></div>
            <div class="h-3 bg-gray-200 rounded-full mt-4"></div>
            <div class="h-3 w-2/3 bg-gray-200 rounded-full"></div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="articles.length === 0" class="py-28 text-center">
        <div class="w-20 h-20 bg-orange-50 rounded-3xl flex items-center justify-center mx-auto mb-6 text-3xl text-orange-300">
          <i class="fa-solid fa-face-sad-tear"></i>
        </div>
        <h3 class="text-2xl font-black text-gray-800 mb-3">Aucun résultat</h3>
        <p class="text-gray-400 mb-8">Essayez avec d'autres mots-clés ou parcourez toutes les catégories.</p>
        <button @click="reset" class="bg-orange-500 text-white px-8 py-3 rounded-xl font-black hover:bg-orange-600 transition-colors">
          Voir tous les articles
        </button>
      </div>

      <template v-else>
        <!-- Featured (page 1, pas de recherche) -->
        <div v-if="page === 1 && !search && articles.length > 0" class="mb-12">
          <a
            :href="`/blog/${articles[0].slug}`"
            class="group grid grid-cols-1 lg:grid-cols-2 rounded-[2rem] overflow-hidden border border-gray-100 hover:shadow-2xl hover:shadow-gray-200/70 hover:-translate-y-1 transition-all duration-500 bg-gray-50"
          >
            <div class="relative overflow-hidden min-h-[280px]">
              <img :src="articles[0].image_url" :alt="articles[0].title" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 absolute inset-0" />
              <div class="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              <span class="absolute top-5 left-5 bg-orange-500 text-white text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full">À la une</span>
            </div>
            <div class="p-10 lg:p-14 flex flex-col justify-center">
              <div class="flex items-center gap-3 mb-5">
                <span class="text-xs font-black uppercase tracking-widest text-orange-600">CVC</span>
                <span class="w-1 h-1 rounded-full bg-gray-300"></span>
                <span class="text-xs text-gray-400">{{ formatDate(articles[0].created_at) }}</span>
              </div>
              <h2 class="text-3xl font-black text-gray-900 leading-tight mb-5 group-hover:text-orange-600 transition-colors">{{ articles[0].title }}</h2>
              <p class="text-gray-500 leading-relaxed mb-8 line-clamp-3 text-sm">{{ stripHtml(articles[0].content) }}</p>
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white text-sm font-black">T</div>
                <div>
                  <p class="text-sm font-bold text-gray-800">Équipe Termoflow</p>
                  <p class="text-xs text-gray-400">{{ readTime(articles[0].content) }} min de lecture</p>
                </div>
                <span class="ml-auto text-sm font-bold text-orange-600 flex items-center gap-2 group-hover:gap-3 transition-all">
                  Lire <i class="fa-solid fa-arrow-right text-xs"></i>
                </span>
              </div>
            </div>
        </a>
        </div>

        <!-- Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <a
            v-for="article in gridArticles" :key="article.id"
            :href="`/blog/${article.slug}`"
            class="group bg-white rounded-[1.75rem] border border-gray-100 overflow-hidden hover:shadow-xl hover:shadow-gray-200/60 hover:-translate-y-1.5 transition-all duration-300"
          >
            <div class="relative overflow-hidden aspect-[16/9]">
              <img :src="article.image_url" :alt="article.title" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-600" />
              <div class="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              <div class="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-lg">
                <i class="fa-regular fa-clock mr-1"></i>{{ readTime(article.content) }} min
              </div>
            </div>
            <div class="p-7">
              <div class="flex items-center gap-2 mb-4">
                <span class="text-xs font-black uppercase tracking-wider text-orange-600">CVC</span>
                <span class="w-1 h-1 rounded-full bg-gray-200"></span>
                <span class="text-xs text-gray-400">{{ formatDate(article.created_at) }}</span>
              </div>
              <h3 class="font-black text-gray-900 text-lg leading-tight mb-3 group-hover:text-orange-600 transition-colors line-clamp-2">{{ article.title }}</h3>
              <p class="text-sm text-gray-400 leading-relaxed line-clamp-2 mb-6">{{ stripHtml(article.content) }}</p>
              <div class="flex items-center justify-between pt-4 border-t border-gray-100">
                <div class="flex items-center gap-2">
                  <div class="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white text-xs font-black">T</div>
                  <span class="text-xs font-bold text-gray-500">Équipe Termoflow</span>
                </div>
                <span class="text-xs font-bold text-orange-500 flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                  Lire <i class="fa-solid fa-arrow-right text-[10px]"></i>
                </span>
              </div>
            </div>
        </a>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.last_page > 1" class="mt-16 flex items-center justify-center gap-2">
          <button @click="goTo(page - 1)" :disabled="page === 1" class="w-11 h-11 rounded-xl border border-gray-200 flex items-center justify-center text-gray-500 hover:border-orange-500 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
            <i class="fa-solid fa-chevron-left text-sm"></i>
          </button>
          <div v-for="p in pages" :key="p">
            <span v-if="p === '...'" class="w-11 h-11 flex items-center justify-center text-gray-400 font-black">…</span>
            <button v-else @click="goTo(p)" :class="['w-11 h-11 rounded-xl font-black text-sm transition-all', p === page ? 'bg-gray-900 text-white shadow-lg' : 'border border-gray-200 text-gray-600 hover:border-orange-500 hover:text-orange-500']">{{ p }}</button>
          </div>
          <button @click="goTo(page + 1)" :disabled="page === pagination.last_page" class="w-11 h-11 rounded-xl border border-gray-200 flex items-center justify-center text-gray-500 hover:border-orange-500 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
            <i class="fa-solid fa-chevron-right text-sm"></i>
          </button>
        </div>
      </template>
    </section>

    <!-- NEWSLETTER -->
    <!-- <section class="py-24 bg-orange-50 border-t border-orange-100">
      <div class="max-w-4xl mx-auto px-4 text-center">
        <div class="inline-flex items-center gap-2 bg-orange-100 px-4 py-2 rounded-full mb-8">
          <i class="fa-solid fa-bell text-orange-500 text-xs"></i>
          <span class="text-xs font-black uppercase tracking-widest text-orange-700">Restez informé</span>
        </div>
        <h2 class="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
          Ne manquez aucune<br>
          <span class="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">mise à jour réglementaire.</span>
        </h2>
        <form @submit.prevent="subscribe" class="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto mt-10">
          <input v-model="email" type="email" placeholder="votre@email.fr" required class="flex-1 px-6 py-4 rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-orange-500 text-gray-800 font-medium transition-colors" />
          <button type="submit" class="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-2xl font-black transition-all hover:-translate-y-0.5 shadow-lg shadow-orange-500/30 whitespace-nowrap">
            S'abonner <i class="fa-solid fa-arrow-right ml-2"></i>
          </button>
        </form>
        <p class="text-xs text-gray-400 mt-4 font-medium">Pas de spam. Désabonnement en 1 clic. RGPD ✓</p>
      </div>
    </section> -->

    <landing-footer />
  </main>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'

export default {
  name: 'BlogIndex',
  components: { LandingHeader, LandingFooter },
  data() {
    return {
      articles: [],
      loading: true,
      searching: false,
      search: '',
      activeTag: null,
      sort: 'recent',
      page: 1,
      email: '',
      searchTimer: null,
      pagination: { total: 0, last_page: 1, per_page: 9 },
      quickTags: ['Réglementation', 'F-Gaz', 'Conformité', 'Gestion', 'Terrain', 'PAC'],
      sortOptions: [
        { label: 'Récents',    value: 'recent'  },
        { label: 'Populaires', value: 'popular' },
      ],
    }
  },
  computed: {
    gridArticles() {
      return this.page === 1 && !this.search ? this.articles.slice(1) : this.articles
    },
    pages() {
      const total = this.pagination.last_page
      const cur   = this.page
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
      const arr = [1]
      if (cur > 3) arr.push('...')
      for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) arr.push(i)
      if (cur < total - 2) arr.push('...')
      arr.push(total)
      return arr
    },
  },
  mounted() { this.fetch() },
  methods: {
    async fetch() {
      this.loading = true
      const response = await this.$request.post('/blog', {
        page: this.page, per_page: 9, sort: this.sort,
        ...(this.search    && { search: this.search }),
        ...(this.activeTag && { tag: this.activeTag }),
      })
      this.articles   = response.data     || []
      this.pagination = { total: response.total, last_page: response.last_page, per_page: response.per_page }
      this.loading    = false
      this.searching  = false
    },
    onSearch() {
      this.searching = true
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => { this.page = 1; this.activeTag = null; this.fetch() }, 420)
    },
    toggleTag(tag) { this.activeTag = this.activeTag === tag ? null : tag; this.search = ''; this.page = 1; this.fetch() },
    setSort(v)     { this.sort = v; this.page = 1; this.fetch() },
    goTo(p) {
      if (p < 1 || p > this.pagination.last_page) return
      this.page = p; this.fetch()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    reset()      { this.search = ''; this.activeTag = null; this.page = 1; this.fetch() },
    subscribe()  { this.$request.post('/newsletter', { email: this.email }); this.email = '' },
    stripHtml(html = '') { return new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim().slice(0, 200) || '' },
    readTime(c = '')     { return Math.max(1, Math.ceil(this.stripHtml(c).split(/\s+/).length / 200)) },
    formatDate(d)        { return d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '' },
  },
}
</script>

<style scoped>
.line-clamp-2 { display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden }
.line-clamp-3 { display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden }
</style>